import flow from "lodash/fp/flow";
import negate from "lodash/fp/negate";
import join from "lodash/fp/join";
import omit from "lodash/fp/omit";
import mapValues from "lodash/fp/mapValues";
import mapKeys from "lodash/mapKeys";
import isNull from "lodash/fp/isNull";
import pickBy from "lodash/fp/pickBy";
import isUndefined from "lodash/fp/isUndefined";

const ignoreValuesFor = [
  "full-right-hand",
  "set-colors",
  "toe-alignment",
  "t-trap",
  "t-trap-reinforced",
  "pocket-lacing-type",
  "pocket-lace--glove",
  "t-spine-lacing-type",
  "t-spine-lace--glove",
  "internal-fit",
  "pad-size",
  "personalizedName",
  "noPersonalization",
  "center-channel",
  "knee-block-and-calf-plate",
  "top-welting",
  "bottom-welting",
  "back-cover",
  "binding",
  "elastic-and-velcro",
  "construction-lace",
  "top-spear",
  "bottom-spear",
  "calf-wrap",
];

const handleFullSetColors = (userChoiceValues) =>
  mapKeys(userChoiceValues, (value, key) =>
    key.endsWith("-color") ? key + "--pads" : key
  );

export default function getGoalieGearAddToOrderValues({
  userChoiceValues,
  product,
  materialID,
  state,
}) {
  const addRequestedNamingConvention = (userChoices) => ({
    ...userChoices,
    handedness: userChoiceValues["full-right-hand"]
      ? "full right"
      : "regular" || null,
    "toe-attachment-combined": userChoiceValues["toe-alignment"]
      ? join("-")([
          userChoiceValues["toe-attachment"],
          userChoiceValues["toe-alignment"],
        ])
      : userChoiceValues["toe-attachment"] || null,
    "pocket-t-trap": userChoiceValues["t-trap-reinforced"]
      ? join("-")([
          userChoiceValues["t-trap"],
          userChoiceValues["t-trap-reinforced"],
        ])
      : userChoiceValues["t-trap"] || null,
    "pocket-lacing": userChoiceValues["pocket-lacing-type"] || null,
    "pocket-lacing-combined":
      join("-")([
        userChoiceValues["pocket-lacing-type"],
        userChoiceValues["pocket-lace--glove"],
      ]) || null,
    "t-spine-lacing":
      userChoiceValues["t-trap"] === "Single"
        ? "N/A"
        : userChoiceValues["t-spine-lacing-type"] || null,
    "t-spine-lacing-combined":
      userChoiceValues["t-trap"] === "Single"
        ? "N/A"
        : join("-")([
            userChoiceValues["t-spine-lacing-type"],
            userChoiceValues["t-spine-lace--glove"],
          ]) || null,
    "hand-size": userChoiceValues["internal-fit"] || null,
    "size-value": userChoiceValues["pad-size"] || null,
    "personalized-name": userChoiceValues["personalizedName"] || null,
    "knee-block-color": userChoiceValues["knee-block-and-calf-plate"] || null,
    "calf-plate-color": userChoiceValues["knee-block-and-calf-plate"] || null,
    "no-personalization": userChoiceValues["noPersonalization"] || null,
    "center-channel-color": userChoiceValues["center-channel"] || null,
    "top-welting-color": userChoiceValues["top-welting"] || null,
    "bottom-welting-color": userChoiceValues["bottom-welting"] || null,
    "calf-wrap-color": userChoiceValues["calf-wrap"] || null,
    "back-cover-color": userChoiceValues["back-cover"] || null,
    "binding-color": userChoiceValues["binding"] || null,
    "elastic-and-velcro-color": userChoiceValues["elastic-and-velcro"] || null,
    "construction-lace-color": userChoiceValues["construction-lace"] || null,
    "top-spear-color": userChoiceValues["top-spear"] || null,
    "bottom-spear-color": userChoiceValues["bottom-spear"] || null,
  });

  const gloveAndBlockerColorChoices = {
    "middle-zone-color--glove":
      userChoiceValues["middle-zone-color--glove"] ||
      userChoiceValues["middle-zone-color"] ||
      null,
    "inside-zone-color--glove":
      userChoiceValues["inside-zone-color--glove"] ||
      userChoiceValues["inside-zone-color"] ||
      null,
    "outside-zone-color--glove":
      userChoiceValues["outside-zone-color--glove"] ||
      userChoiceValues["outside-zone-color"] ||
      null,
    "top-zone-color--glove":
      userChoiceValues["top-zone-color--glove"] ||
      userChoiceValues["top-zone-color"] ||
      null,
    "line-zone-color--glove":
      userChoiceValues["line-zone-color--glove"] ||
      userChoiceValues["line-zone-color"] ||
      null,
    "center-zone-color--glove":
      userChoiceValues["center-zone-color--glove"] ||
      userChoiceValues["center-zone-color"] ||
      null,
    "top-spear-color--glove":
      userChoiceValues["top-spear--glove"] ||
      userChoiceValues["top-spear"] ||
      null,
    "bottom-spear-color--glove":
      userChoiceValues["bottom-spear--glove"] ||
      userChoiceValues["bottom-spear"] ||
      null,
    "bauer-logo-color--glove":
      userChoiceValues["bauer-logo-color--glove"] ||
      userChoiceValues["bauer-logo-color"] ||
      null,
    "hyp2rlite-logo-color--glove":
      userChoiceValues["hyp2rlite-logo-color--glove"] ||
      userChoiceValues["hyp2rlite-logo-color"] ||
      null,
    "mach-logo-color--glove":
      userChoiceValues["mach-logo-color--glove"] ||
      userChoiceValues["mach-logo-color"] ||
      null,

    "binding-color--glove":
      userChoiceValues["binding--glove"] || userChoiceValues["binding"] || null,
    "elastic-and-velcro-color--glove":
      userChoiceValues["elastic-and-velcro--glove"] ||
      userChoiceValues["elastic-and-velcro"] ||
      null,
    "construction-lace-color--glove":
      userChoiceValues["construction-lace--glove"] ||
      userChoiceValues["construction-lace"] ||
      null,

    "pocket-lace-color--glove": userChoiceValues["pocket-lace--glove"] || null,

    "t-spine-lace-color--glove":
      userChoiceValues["t-trap"] === "Single"
        ? "N/A"
        : userChoiceValues["t-spine-lace--glove"] || null,

    "middle-zone-color--blocker":
      userChoiceValues["middle-zone-color--blocker"] ||
      userChoiceValues["middle-zone-color"] ||
      null,
    "inside-zone-color--blocker":
      userChoiceValues["inside-zone-color--blocker"] ||
      userChoiceValues["inside-zone-color"] ||
      null,
    "outside-zone-color--blocker":
      userChoiceValues["outside-zone-color--blocker"] ||
      userChoiceValues["outside-zone-color"] ||
      null,
    "top-zone-color--blocker":
      userChoiceValues["top-zone-color--blocker"] ||
      userChoiceValues["top-zone-color"] ||
      null,
    "line-zone-color--blocker":
      userChoiceValues["line-zone-color--blocker"] ||
      userChoiceValues["line-zone-color"] ||
      null,
    "center-zone-color--blocker":
      userChoiceValues["center-zone-color--blocker"] ||
      userChoiceValues["center-zone-color"] ||
      null,
    "top-spear-color--blocker":
      userChoiceValues["top-spear--blocker"] ||
      userChoiceValues["top-spear"] ||
      null,
    "bottom-spear-color--blocker":
      userChoiceValues["bottom-spear--blocker"] ||
      userChoiceValues["bottom-spear"] ||
      null,
    "bauer-logo-color--blocker":
      userChoiceValues["bauer-logo-color--blocker"] ||
      userChoiceValues["bauer-logo-color"] ||
      null,
    "hyp2rlite-logo-color--blocker":
      userChoiceValues["hyp2rlite-logo-color--blocker"] ||
      userChoiceValues["hyp2rlite-logo-color"] ||
      null,
    "mach-logo-color--blocker":
      userChoiceValues["mach-logo-color--blocker"] ||
      userChoiceValues["mach-logo-color"] ||
      null,

    "supreme-logo-color--blocker":
      userChoiceValues["supreme-logo-color--blocker"] ||
      userChoiceValues["supreme-logo-color"] ||
      null,

    "binding-color--blocker":
      userChoiceValues["binding--blocker"] ||
      userChoiceValues["binding"] ||
      null,
    "elastic-and-velcro-color--blocker":
      userChoiceValues["elastic-and-velcro--blocker"] ||
      userChoiceValues["elastic-and-velcro"] ||
      null,
    "construction-lace-color--blocker":
      userChoiceValues["construction-lace--blocker"] ||
      userChoiceValues["construction-lace"] ||
      null,
  };

  const handleGloveAndBlockerColors = flow(
    (obj) => ({ ...obj, ...gloveAndBlockerColorChoices }),
    pickBy(negate(isNull))
  );

  return flow(
    pickBy(negate(isUndefined)),
    mapValues((val) => val.toString().toLowerCase()),
    omit(ignoreValuesFor),
    addRequestedNamingConvention,
    pickBy(negate(isNull)),
    // Rename all full set colors by appending '--pads'
    handleFullSetColors,
    // Add '--glove' and '--blocker' color values, even if same as full set
    handleGloveAndBlockerColors,
    // Remove brackets and contents AND kebab-case
    mapValues((val) =>
      val
        .toString()
        .replace(/ *\([^)]*\) */g, "")
        .replace(/[\s-]+/g, "-")
    ),
    mapValues((val) => val.toLowerCase()),
    (userChoices) => ({
      ...userChoices,
      "material-id": materialID,
      ProductFamily: product?.productFamily,
      product: state?.app?.formData?.product,
    })
  )(userChoiceValues);
}
